import { ReactElement } from 'react';
// Features
import { SocialMedia } from '@utmb/features/layout/social-media/SocialMedia';
import { StatsRacePick } from '@utmb/features/race/stats-bar/StatsBar';
// Design system
import { Col, Row } from '@utmb/design-system/box/Box';
import { Font } from '@utmb/design-system/text/font';
import { Image } from '@utmb/design-system/image/Image';
import { Link } from '@utmb/design-system/link/link';
import { Pin } from '@utmb/design-system/pin/Pin';
import { Spacer } from '@utmb/design-system/spacer/Spacer';
// Interfaces
import { ColorTypesEnum } from '@utmb/types/colors';
import { LinkView, RaceLogoView, RacePageHeaderView, StatView } from 'src/types';
import { StyleEnum } from '@utmb/types/enum/StyleEnum';
// Styles
import styles from './race-page-header.module.scss';
import { MicroDataEvent } from '@utmb/types/microdata';
import { useCloudinary } from '@utmb/design-system/use-cloudinary/UseCloudinary';
import { ImageFilterEnum } from '@utmb/types/enum/ImageFilterEnum';
import { CloudinaryTransformEnum } from '@utmb/types/cloudinary-transform.enum';
import { WorldSeriesRaceFollow } from '@utmb/features/world-series/follow/WorldSeriesRaceFollow';

// Page Header - Interface
export interface PageHeaderProps extends RacePageHeaderView {
    raceId: number;
    raceLink?: LinkView;
    raceLogo: RaceLogoView;
    mainStats?: StatView[];
    place?: string;
}

// Page Header

export const RacePageHeader = (props: PageHeaderProps): ReactElement => {
    // Props
    const { media, raceId, raceLink, raceLogo, startDateIso, endDateIso, summary, title, mainStats, place, imageFilter } = props;
    const { getImage } = useCloudinary();

    // CSS Variables
    //const raceCSS = ['font-white', 'font-uppercase', 'ls-1', styles.race_CSS].filter(Boolean).join(' ');
    const summaryCSS = ['font-white', 'lh-30', 'lh-d-50', 'fw-5', styles.ph_summary].filter(Boolean).join(' ');

    const microdataEvent: MicroDataEvent | null = startDateIso
        ? {
              '@context': 'https://schema.org',
              '@type': 'Event',
              name: title,
              description: summary,
              startDate: startDateIso,
              endDate: endDateIso,
              eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
              eventStatus: 'https://schema.org/EventScheduled',
              location: {
                  '@type': 'Place',
                  name: place ?? '',
              },
              image: [
                  raceLogo?.light?.publicId ? getImage(raceLogo.light.publicId, { maxWidth: 720 }) : '',
                  getImage(media.publicId, { maxWidth: 1920 }),
              ].filter(Boolean),
          }
        : null;

    // JSX
    return (
        <>
            {/* microdata */}
            {microdataEvent && (
                <script type="application/ld+json" suppressHydrationWarning dangerouslySetInnerHTML={{ __html: JSON.stringify(microdataEvent) }} />
            )}
            {/* Container */}
            <Col className={styles.container}>
                {raceId ? <WorldSeriesRaceFollow raceId={raceId} className={styles.follow} /> : null}
                {/* BG Image */}
                <Image
                    objectFit="cover"
                    className={[styles.img, imageFilter === ImageFilterEnum.SIMPLE_ALPHA ? styles.filter : null].filter(Boolean).join(' ')}
                    image={media}
                    objectPosition="center center"
                    addOptions={imageFilter === ImageFilterEnum.EPIC ? [CloudinaryTransformEnum.EPIC] : undefined}
                />
                {/* Mobile Logo */}
                {raceLogo && (
                    <>
                        <div className={styles.img_logo_wrapper}>
                            <Image objectFit="contain" layout="fill" className={styles.mobile_logo} image={raceLogo.light} />
                        </div>
                        <Spacer.size20 />
                    </>
                )}

                {/* 1 - MAIN CONTENT */}
                <section className={styles.page_header_pcenter}>
                    {/* [ 2 - TITLE ] */}
                    {title && (
                        <Font.FuturaBook mobile="36" desktop="80" className={styles.ph_title} as="h1">
                            {title}
                        </Font.FuturaBook>
                    )}

                    {/* [ 3 - SUMMARY ] */}
                    {summary && (
                        <Row>
                            <Font.FuturaLight mobile="22" desktop="36" className={summaryCSS}>
                                {summary}
                            </Font.FuturaLight>
                        </Row>
                    )}
                </section>

                {/* [ 5 - SOCIAL MEDIA ] */}
                <Pin className={styles.social_share} xy={{ top: '50%', right: '50px', transform: 'translateY(-50%)' }}>
                    <SocialMedia.Vertical className="landscape-only" colorTheme={ColorTypesEnum.WHITE} />
                </Pin>

                {/* [ 6 - MAINSTATS - [RACE ONLY] ] */}
                {mainStats && (
                    <div className={styles.stats}>
                        <StatsRacePick style={StyleEnum.NORMAL} stats={mainStats.map((stat) => ({ ...stat, isDark: true }))} />
                    </div>
                )}

                {/* [ 7 - ACTION - [RACE ONLY] ] */}
                {raceLink && (
                    <div>
                        <Spacer.size30 />
                        <Link.Button
                            label={raceLink.label}
                            href={raceLink.slug}
                            color={ColorTypesEnum.DEFAULT}
                            bgcolor={ColorTypesEnum.WHITE}
                            variant="right"
                            composeClassName={styles.link}
                            followClassName={`${raceLink.followClass}-ga`}
                        />
                    </div>
                )}
            </Col>
        </>
    );
};
